import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import RefreshIcon from '@mui/icons-material/Refresh'
import Refreshing from 'components/core/Refreshing'
import UserFilter from '../StreamIngest/UserFilter'
import { WINDOWS } from '../StreamIngest/vocabulary'
import {
  FlowLayout,
  StarMetric,
  SummaryLine,
  accountFrom,
  accountParams,
  useVertical,
} from '../Flow/parts'
import { loopVerdict, pipelineVerdict } from '../Flow/verdicts'
import { useLoopReads, usePipelineReads } from '../Flow/reads'
import PipelinePanel from '../Flow/PipelinePanel'
import LoopPanel from '../Flow/LoopPanel'
import HowThisWorks from '../Flow/HowThisWorks'

// The Overview page: the whole product on one screen, as the two rows the
// Pipeline and Loop pages each open with (`Main/Flow`, whose README says where
// each part lives). The fleet or one account, without the Pipeline's
// seal-cause and client-build strata: those narrow spans, and the whole
// product is not a property of a span. The loop is read for the fleet only,
// and says so under an account.
//
// Each row is read once here (`Flow/reads.js`): the loop takes the pipeline's
// latency answer rather than reading its own.
//
// The account is the URL's (`?user=…&name=…`), so the Users page links here
// narrowed to one person, and every link on both rows carries it on.

// On a phone the panels stack and the loop's would sit a scroll away, so both
// rows' sentences and the product's number lead the page in a summary of
// their own, and the panels leave them out (`summarized`).
const PhoneSummary = ({ pipelineReads, loopReads, user }) => {
  const vertical = useVertical()
  if (!vertical) return null
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
        <SummaryLine verdict={pipelineVerdict(pipelineReads, { user })} />
        <SummaryLine
          verdict={loopVerdict(loopReads, { fleetOnly: Boolean(user) })}
        />
      </Box>
      <StarMetric payload={pipelineReads.payload} />
    </Paper>
  )
}

const Overview = () => {
  // A week: `1d` is today so far in UTC, which on an evening in the US is a
  // few hours old and can read as nothing having streamed at all.
  const [windowDays, setWindowDays] = useState(7)
  const [searchParams, setSearchParams] = useSearchParams()
  const [user, setUser] = useState(() => accountFrom(searchParams))
  const pickUser = (next) => {
    setUser(next)
    setSearchParams(accountParams(next), { replace: true })
  }
  const pipelineReads = usePipelineReads({
    days: windowDays,
    userId: user?.id || null,
  })
  const loopReads = useLoopReads({
    days: windowDays,
    enabled: !user,
    latency: pipelineReads.payload,
  })
  const loading = pipelineReads.loading || loopReads.loading

  return (
    <Box
      sx={{
        px: { mobile: 2, desktop: 6 },
        pt: { mobile: 2, desktop: 4 },
        // Room under "How this works", so it never sits on the window's edge.
        // Sized to its content, not stretched to `main`'s height, or the
        // content runs past the box and the padding is left behind.
        pb: { mobile: 8, desktop: 12 },
        alignSelf: 'flex-start',
        // Fills its column: the layout would shrink it to its content, and
        // the flow turns on the room it is given.
        width: '100%',
        maxWidth: 1600,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Overview
        </Typography>
        {loading && <CircularProgress size={18} />}
        <Box sx={{ flex: 1 }} />
        <UserFilter user={user} onChange={pickUser} />
        <ToggleButtonGroup
          value={windowDays}
          exclusive
          size="small"
          onChange={(event, next) => next && setWindowDays(next)}
        >
          {WINDOWS.map(({ days: value, label, title }) => (
            <ToggleButton key={value} value={value} title={title}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={() => {
              pipelineReads.refresh()
              loopReads.refresh()
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The whole product, per UTC day
        {user ? ` · ${user.username || user.id}` : ' · fleet'}
        {pipelineReads.payload?.as_of
          ? ` · as of ${dayjs(pipelineReads.payload.as_of).format(
              'HH:mm:ss'
            )} (cached 60s)`
          : ''}
      </Typography>

      {pipelineReads.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {pipelineReads.error}
        </Alert>
      )}

      <FlowLayout>
        <PhoneSummary
          pipelineReads={pipelineReads}
          loopReads={loopReads}
          user={user}
        />
        <Refreshing active={pipelineReads.loading}>
          <PipelinePanel reads={pipelineReads} user={user} summarized />
        </Refreshing>
        <Refreshing active={loopReads.loading}>
          <LoopPanel
            reads={loopReads}
            fleetOnly={Boolean(user)}
            user={user}
            summarized
          />
        </Refreshing>
        <HowThisWorks />
      </FlowLayout>
    </Box>
  )
}

export default Overview
